/**
 * The signed-in tenancy's ENFORCED visibility policy, and the policy table it
 * narrows.
 *
 * A preference (Settings › Default visibility) decides where a choice starts; a
 * policy — set by an operator in the admin portal, read here from management-api
 * `GET /policies` — decides which choices exist. It is applied by narrowing
 * {@link OVERLAY_CONFIG} (`applyEnforcedPolicy`), because that table is what the
 * Settings panel, the attach control, the record dialogs and the saved-default
 * resolution already read: one narrowing reaches all of them, and a personal or
 * workspace default the policy forbids falls through exactly like a withdrawn
 * rung instead of being written at.
 *
 * ## What this is not
 *
 * Enforcement. The API refuses a write the policy forbids (`VISIBILITY_POLICY`);
 * this is the UX that keeps a person from composing one. So a read that fails
 * narrows nothing and says so (`status: "unreadable"`) — inventing a lock the
 * server never set would be as wrong as hiding one it did.
 *
 * ## Freshness
 *
 * One small GET per tenancy, trusted for {@link ENFORCED_POLICY_FRESH_MS}: the
 * same as the server-side reader's cache, so a lock an operator adds reaches an
 * open tab in about the time it reaches the API.
 */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  NO_POLICY,
  applyEnforcedPolicy,
  enforcedBySentence,
  enforcedWriteOf,
  isWriteBlocked,
  policyFor,
  type AttributionPolicies,
  type AttributionResource,
  type AttributionScope,
  type EnforcedPolicy,
} from "@authe/fe-commons/attribution";
import type { StorageIdentity } from "@authe/fe-commons/storage";
import { usePoliciesSource } from "@/api/adapters";
import { OVERLAY_CONFIG } from "@/config";
import { useStorageIdentity } from "./storageIdentity";

/** One entry per tenancy (no user: the user is who a policy constrains). */
export function enforcedPolicyQueryKey(identity: StorageIdentity) {
  return ["enforced-policy", identity.customerId, identity.orgId, identity.tenantId] as const;
}

export const ENFORCED_POLICY_FRESH_MS = 60_000;

export type EnforcedPolicyStatus = "loading" | "ready" | "unreadable";

export interface EnforcedPolicyState {
  policy: EnforcedPolicy;
  status: EnforcedPolicyStatus;
}

export function useEnforcedPolicy(): EnforcedPolicyState {
  const source = usePoliciesSource();
  const identity = useStorageIdentity();
  const query = useQuery({
    queryKey: enforcedPolicyQueryKey(identity),
    queryFn: ({ signal }) => source.getPolicies(signal),
    enabled: Boolean(identity.customerId || identity.orgId || identity.tenantId),
    staleTime: ENFORCED_POLICY_FRESH_MS,
    refetchInterval: ENFORCED_POLICY_FRESH_MS,
    retry: 1,
  });
  return React.useMemo<EnforcedPolicyState>(() => {
    // A refetch that fails keeps the last good answer (react-query keeps
    // `data`), so a blip does not unlock what was locked a minute ago.
    if (query.data) {
      return { policy: query.data, status: query.data.readable ? "ready" : "unreadable" };
    }
    if (query.isError) return { policy: { ...NO_POLICY, readable: false }, status: "unreadable" };
    return { policy: NO_POLICY, status: query.fetchStatus === "idle" ? "ready" : "loading" };
  }, [query.data, query.isError, query.fetchStatus]);
}

/**
 * The policy table every surface reads: this app's, narrowed by what is enforced.
 *
 * The SAME object as {@link OVERLAY_CONFIG} when nothing is enforced, so a
 * tenancy with no rules re-renders nothing.
 */
export function useDashboardPolicies(): AttributionPolicies {
  const { policy } = useEnforcedPolicy();
  return React.useMemo(() => applyEnforcedPolicy(OVERLAY_CONFIG, policy), [policy]);
}

/**
 * The record tier a visibility rung is stored at.
 *
 * Authored records spell their rung as a partition (`user` / `tenant` / …), and
 * both personal rungs land on `user` — `team` is the same row plus carriers —
 * which is `recordVisibility`'s mapping, restated for a FILTER rather than a
 * write.
 */
const RECORD_TIER: Record<AttributionScope, "user" | "tenant" | "org" | "customer"> = {
  private: "user",
  team: "user",
  tenant: "tenant",
  org: "org",
  customer: "customer",
};

export interface RecordScopePolicy<T extends string> {
  /** The record tiers still allowed, or `undefined` when nothing is enforced. */
  allowed: readonly T[] | undefined;
  /** "Enforced by your …", when a rule is in force. */
  note: string | undefined;
  /** True when the rule leaves nothing to write at. */
  blocked: boolean;
}

/**
 * What an authored-record dialog may offer, as RECORD tiers.
 *
 * For the dialogs that carry their own picker in their own vocabulary (a
 * tracer's "Only me / My tenant / …", a saved view's Personal / Shared): they
 * filter their options through `allowed` and print `note`.
 */
export function useRecordScopePolicy<T extends string>(
  resource: AttributionResource,
): RecordScopePolicy<T> {
  const policies = useDashboardPolicies();
  return React.useMemo(() => {
    const policy = policyFor(resource, policies);
    const rule = enforcedWriteOf(policy);
    if (!rule) return { allowed: undefined, note: undefined, blocked: false };
    const allowed = [...new Set(policy.writeScopes.map((scope) => RECORD_TIER[scope]))] as T[];
    return { allowed, note: `${enforcedBySentence(rule)}.`, blocked: isWriteBlocked(policy) };
  }, [policies, resource]);
}
